// Thin wrapper around fetch with bearer auth.
//
// The API base comes from Vite env at build time. The read token is
// supplied by the operator at runtime and stored in localStorage, so the
// public frontend bundle does not contain backend credentials.

const BASE =
  (import.meta.env.VITE_TRACE_API_BASE as string | undefined) ?? "";

export const TRACE_TOKEN_KEY = "swarm_trace_read_token";

export function getTraceToken(): string {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(TRACE_TOKEN_KEY) ?? "";
}

export function setTraceToken(token: string) {
  window.localStorage.setItem(TRACE_TOKEN_KEY, token.trim());
}

export function clearTraceToken() {
  window.localStorage.removeItem(TRACE_TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string): Promise<T> {
  if (!BASE) {
    throw new ApiError(
      0,
      "VITE_TRACE_API_BASE is not set — frontend cannot reach the trace server.",
    );
  }
  const token = getTraceToken();
  if (!token) {
    throw new ApiError(401, "Trace read token required.");
  }
  const r = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    throw new ApiError(r.status, await r.text().catch(() => r.statusText));
  }
  return (await r.json()) as T;
}

// ─── Server response types (mirror swarm-trace-server JSON) ───────────────

export interface TaskRow {
  id: string;
  name: string;
  created_at: string;
  last_seen_at: string;
  runs_count: number;
  active_runs: number;
}

export interface UsageStats {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
  turn_usage?: TurnUsage[];
}

export interface TurnUsage {
  index: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

export interface RunRow {
  id: string;
  task_id: string;
  agent_id: string;
  agent_name: string;
  node_id: string | null;
  graph_id: string | null;
  status: "running" | "succeeded" | "failed" | "cancelled";
  started_at: string | null;
  ended_at: string | null;
  error: string | null;
  usage: UsageStats | null;
}

export interface RunDetail extends RunRow {
  input: unknown;
  output: unknown;
}

export interface EventRow {
  seq: number;
  kind: string;
  body: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  at: string;
}
